import {
  BookOpen,
  Clock,
  FileSignature,
  Inbox,
  LayoutTemplate,
  ListChecks,
  Palette,
  Plus,
  Receipt,
  SearchX,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import {
  LinkBase,
  useCanAccess,
  useCreatePath,
  useGetResourceLabel,
  useListContextWithProps,
  useLocaleState,
  useResourceContext,
  useResourceDefinition,
  useTranslate,
} from "ra-core";
import type { ReactNode } from "react";

import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import "@/styles/list-skeleton.css";
import "@/styles/empty-state.css";

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: ReactNode;
  hint?: ReactNode;
  /** Call to action, e.g. a "New invoice" link. */
  action?: ReactNode;
  className?: string;
}

/**
 * Glass card telling the user a list has nothing to show yet, with an
 * optional hint and call to action. Pure presentation: `ListEmptyState`
 * wires it to the current list and resource.
 */
export const EmptyState = ({
  icon: Icon = Inbox,
  title,
  hint,
  action,
  className,
}: EmptyStateProps) => (
  <div
    role="status"
    data-testid="empty-state"
    className={cn("empty-state record-card", className)}
  >
    <span className="empty-state__icon" aria-hidden>
      <Icon className="size-6" strokeWidth={1.75} />
    </span>
    <p className="empty-state__title">{title}</p>
    {hint ? <p className="empty-state__hint">{hint}</p> : null}
    {action ? <div className="empty-state__action">{action}</div> : null}
  </div>
);

/** Icons of the resources whose lists can be empty. */
const EMPTY_STATE_ICONS: Record<string, LucideIcon> = {
  invoices: Receipt,
  quotes: FileSignature,
  wiki_pages: BookOpen,
  subscriptions: Wallet,
  time_entries: Clock,
  team_tasks: ListChecks,
  tasks: ListChecks,
  delivery_templates: LayoutTemplate,
  styles: Palette,
  sales: Users,
};

/** Resources with their own empty-state copy under `crm.empty_state.resources`. */
const RESOURCES_WITH_COPY = new Set(Object.keys(EMPTY_STATE_ICONS));

const hasActiveFilters = (filterValues: unknown) =>
  !!filterValues &&
  typeof filterValues === "object" &&
  Object.keys(filterValues).length > 0;

export interface ListEmptyStateProps {
  resource?: string;
  className?: string;
  /** Override the list context (e.g. outside a <List>). */
  filterValues?: Record<string, unknown>;
  setFilters?: (filters: Record<string, unknown>, displayed?: string[]) => void;
}

/**
 * Empty state of a list: the resource's own message and hint, a "New X"
 * button when the resource has a create view the user may access, and a
 * "Clear filters" button instead when filters hide every record.
 */
export const ListEmptyState = (props: ListEmptyStateProps) => {
  const resource = useResourceContext(props);
  if (!resource) {
    throw new Error("<ListEmptyState> must be used inside a resource context");
  }
  return <ListEmptyStateView {...props} resource={resource} />;
};

const ListEmptyStateView = ({
  resource,
  className,
  ...listProps
}: ListEmptyStateProps & { resource: string }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const getResourceLabel = useGetResourceLabel();
  const { filterValues, setFilters } = useListContextWithProps(listProps);
  const { hasCreate } = useResourceDefinition({ resource });
  const { canAccess: canCreate } = useCanAccess({
    resource,
    action: "create",
  });
  const createPath = useCreatePath();

  const name = getResourceLabel(resource, 2).toLocaleLowerCase(locale);
  const icon = EMPTY_STATE_ICONS[resource];

  if (hasActiveFilters(filterValues) && setFilters) {
    return (
      <EmptyState
        className={className}
        icon={SearchX}
        title={translate("crm.empty_state.filtered_title")}
        hint={translate("crm.empty_state.filtered_hint", { name })}
        action={
          <button
            type="button"
            className={cn(
              buttonVariants({ variant: "outline" }),
              "rounded-full",
            )}
            onClick={() => setFilters({}, [])}
          >
            {translate("crm.empty_state.clear_filters")}
          </button>
        }
      />
    );
  }

  const hasCopy = RESOURCES_WITH_COPY.has(resource);
  const title = hasCopy
    ? translate(`crm.empty_state.resources.${resource}.title`)
    : translate("crm.empty_state.title", { name });
  const hint = hasCopy
    ? translate(`crm.empty_state.resources.${resource}.hint`)
    : translate("crm.empty_state.hint");

  return (
    <EmptyState
      className={className}
      icon={icon}
      title={title}
      hint={hint}
      action={
        hasCreate && canCreate ? (
          <LinkBase
            to={createPath({ resource, type: "create" })}
            className={cn(buttonVariants(), "rounded-full")}
          >
            <Plus aria-hidden />
            {translate(`resources.${resource}.page.create`, {
              _: translate("ra.page.create", {
                name: getResourceLabel(resource, 1),
              }),
            })}
          </LinkBase>
        ) : null
      }
    />
  );
};
